import React, { useRef, useCallback, useMemo, useEffect } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CheckIcon from "@mui/icons-material/Check";
import { ListItemIcon, InputAdornment, CircularProgress, type Theme, type SxProps } from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import type { CustomSelectProps } from "./types/types";
import {
	formFieldLabelClassName,
	formSelectPlaceholderClassName,
	formSelectValueClassName,
} from "@config/theme/formControls";
import { FormFieldHelperText, FormFieldLabel } from "@components/common/FormField";
import {
	mergeSelectSx,
	menuItemSx,
	selectOutlinedInputSx,
	outlinedInputExternalLabelProps,
	menuItemContentSx,
	menuItemLabelSx,
	listItemIconSx,
	checkIconSx,
} from "./styles/Select.styles";
import { SelectFieldWrapper } from "./shared/SelectFieldWrapper";
import { createMenuProps } from "./styles/MultiSelect.styles";
import { useSingleSelectSearch } from "./hooks/useSingleSelectSearch";
import { SingleSelectSearch } from "./components";
import { MultiSelectNoResults } from "./components";

const CustomSelect: React.FC<CustomSelectProps> = ({
	options,
	selected,
	setSelected,
	label,
	required,
	placeholder,
	id,
	startIcon,
	error = false,
	helperText = "",
	className = "",
	disabled = false,
	onBlur,
	showSearch = false,
	searchPlaceholder = "Search...",
	filterOptionsLocally = true,
	onSearchTermChange,
	maxHeight = 300,
	hasNextPage = false,
	onLoadMore,
	isLoadingMore = false,
	sx,
	...rest
}) => {
	// Ensure options is always an array and filter out invalid options immediately
	// Allow placeholder options (value="") but filter out truly invalid ones
	const safeOptions = useMemo(() => {
		if (!Array.isArray(options)) return [];
		return options.filter(opt => {
			// Must be a valid object
			if (!opt || typeof opt !== 'object' || opt === null) return false;
			// Must have value property (can be empty string for placeholders)
			if (!('value' in opt)) return false;
			// Value can be empty string (for placeholders) but not undefined or null
			return opt.value !== undefined && opt.value !== null;
		});
	}, [options]);
	
	const { searchTerm, setSearchTerm, filteredOptions, resetSearch } = useSingleSelectSearch(
		safeOptions,
		filterOptionsLocally,
	);
	const menuRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!showSearch || filterOptionsLocally || !onSearchTermChange) return;
		onSearchTermChange(searchTerm);
	}, [searchTerm, showSearch, filterOptionsLocally, onSearchTermChange]);

	// Get options to display - filtered if search is enabled, all options otherwise
	// Additional safety check (though safeOptions is already filtered)
	const displayOptions = useMemo(() => {
		const source = showSearch ? filteredOptions : safeOptions;
		return source.filter(opt => {
			// Must be valid object
			if (!opt || typeof opt !== 'object' || opt === null) return false;
			// Must have value property
			if (!('value' in opt)) return false;
			// Value can be empty string (for placeholders) but not undefined or null
			return opt.value !== undefined && opt.value !== null;
		});
	}, [showSearch, filteredOptions, safeOptions]);

	const handleClose = () => {
		resetSearch();
		if (!filterOptionsLocally) {
			onSearchTermChange?.("");
		}
	};

	// Handle scroll for pagination
	const handleMenuScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const scrollTop = target.scrollTop;
		const scrollHeight = target.scrollHeight;
		const clientHeight = target.clientHeight;

		// Load more when scrolled to bottom (within 50px)
		if (hasNextPage && !isLoadingMore && scrollHeight - scrollTop - clientHeight < 50) {
			onLoadMore?.();
		}
	}, [hasNextPage, isLoadingMore, onLoadMore]);

	return (
		<>
			{label && (
				<FormFieldLabel htmlFor={id} required={required} className={formFieldLabelClassName}>
					{label}
				</FormFieldLabel>
			)}
			<SelectFieldWrapper>
			<Select
				value={selected || ""} // Ensure value is never undefined
				onChange={e => {
					const selectedValue = e.target.value as string;
					// Prevent selecting placeholder option
					if (!selectedValue || selectedValue === "") {
						return;
					}
					setSelected(selectedValue);
					// Reset search after selection
					resetSearch();
				}}
				onBlur={onBlur}
				onClose={handleClose}
				fullWidth
				displayEmpty
				error={error}
				disabled={disabled}
				className={className}
				sx={(sx ? [mergeSelectSx(error), sx] : mergeSelectSx(error)) as SxProps<Theme>}
				{...rest}
				MenuProps={{
					...createMenuProps(maxHeight),
					PaperProps: {
						...createMenuProps(maxHeight).PaperProps,
						onScroll: handleMenuScroll,
						ref: menuRef,
					},
				}}
				input={
					<OutlinedInput
						{...outlinedInputExternalLabelProps}
						sx={selectOutlinedInputSx(error)}
						startAdornment={
							startIcon ? (
								<InputAdornment sx={{ marginRight: "-3px" }} position="start">
									{startIcon}
								</InputAdornment>
							) : undefined
						}
					/>
				}
				renderValue={val => {
					if (!val || val === "") {
						const placeholderOption = safeOptions.find(opt => opt && opt.isPlaceholder);
						return (
							<span className={formSelectPlaceholderClassName}>
								{placeholderOption?.label || placeholder}
							</span>
						);
					}
					const valStr = String(val);
					const opt = safeOptions.find(o => o && o.value !== undefined && o.value !== null && String(o.value) === valStr);
					return (
						<span className={formSelectValueClassName}>{opt ? (opt.label || opt.name || "") : ""}</span>
					);
				}}>
				{/* Search Input - Always render with value prop */}
				{showSearch ? (
					<SingleSelectSearch
						placeholder={searchPlaceholder}
						searchTerm={searchTerm}
						onSearchChange={setSearchTerm}
					/>
				) : null}

				{/* Options - Use React.Children.toArray to ensure all are valid */}
				{React.Children.toArray(
					displayOptions
						.filter(opt => {
							// Strict validation - only keep completely valid options
							if (!opt || typeof opt !== 'object' || opt === null) return false;
							if (!('value' in opt)) return false;
							const val = opt.value;
							if (val === undefined || val === null) return false;
							// Ensure value can be converted to string
							try {
								const strVal = String(val);
								return strVal !== 'undefined' && strVal !== 'null';
							} catch {
								return false;
							}
						})
						.map((opt, index) => {
							// Double-check before rendering
							if (!opt || !('value' in opt) || opt.value === undefined || opt.value === null) {
								return null;
							}
							const optValue = String(opt.value);
							if (optValue === 'undefined' || optValue === 'null') {
								return null;
							}
							
							return (
								<MenuItem
									key={optValue || `option-${index}`}
									value={optValue}
									disabled={!!opt.isPlaceholder}
									sx={menuItemSx(!!opt.isPlaceholder)}>
									<div style={menuItemContentSx as React.CSSProperties}>
										<span style={menuItemLabelSx as React.CSSProperties}>{opt.name || opt.label || ""}</span>
										{/* Only render the check icon for the selected option (not for placeholder or unselected) */}
										{!opt.isPlaceholder && selected === optValue && (
											<ListItemIcon sx={listItemIconSx}>
												<CheckIcon fontSize="small" sx={checkIconSx} />
											</ListItemIcon>
										)}
									</div>
								</MenuItem>
							);
						})
						.filter(Boolean)
				)}

				{/* Loading more indicator - Always render with value prop */}
				{hasNextPage && isLoadingMore ? (
					<MenuItem disabled value="" sx={{ justifyContent: "center", py: 2 }}>
						<CircularProgress size={20} />
					</MenuItem>
				) : null}

				{/* No results - Always render with value prop */}
				{showSearch && searchTerm && filteredOptions.length === 0 ? <MultiSelectNoResults /> : null}
			</Select>
			</SelectFieldWrapper>
			<FormFieldHelperText error={error}>{helperText}</FormFieldHelperText>
		</>
	);
};

export default CustomSelect;